// Order matters: more specific paths must come before their prefix.
const legacyRedirects: Record<string, string> = {
  // v2 account settings folded notifications into the single /settings page.
  '/settings/notifications': '/settings',
  '/business-intelligence': '/analyze-trade-activity',
  '/token-designer/valuation-documenting/sanitizing-tokenomics': '/design-tokenomics/publish/manage-one-pager',
  '/token-designer': '/design-tokenomics',
  '/dashboard': '/',
  '/home': '/',
}

// Routes with no /project/:id variant, whose scoped URLs must drop the prefix.
const unscopedRoutes = ['/about']

const PROJECT_PREFIX = /^\/project\/[^/]+/i

/**
 * Rewrites a scoped or mixed-case URL of an unscoped route to its canonical path,
 * or returns null when there's nothing to rewrite. Also runs on the server, so
 * these URLs redirect before any HTML is sent.
 */
export function resolveUnscopedRouteRedirect(path: string, fullPath: string): string | null {
  const projectPrefix = path.match(PROJECT_PREFIX)?.[0] ?? ''
  const routePath = path.slice(projectPrefix.length)

  // vue-router matches case-insensitively but routeRules don't, so /About/pricing only server-renders once lowercased.
  const normalizedRoutePath = routePath.toLowerCase()
  if (!unscopedRoutes.some(route => normalizedRoutePath === route || normalizedRoutePath.startsWith(`${route}/`))) {
    return null
  }
  const target = `${normalizedRoutePath}${fullPath.slice(projectPrefix.length + routePath.length)}`
  return target === fullPath ? null : target
}

/**
 * Rewrites a legacy path to its successor, or returns null when nothing matches.
 * The legacy path may sit behind a /project/:id prefix; the match is anchored
 * right after it (or at the root) so nested occurrences like
 * /market-maker-monitoring/dashboard are left alone.
 */
export function resolveLegacyRedirect(path: string, fullPath: string): string | null {
  const unscopedRedirect = resolveUnscopedRouteRedirect(path, fullPath)
  if (unscopedRedirect) {
    return unscopedRedirect
  }

  const projectPrefix = path.match(PROJECT_PREFIX)?.[0] ?? ''
  const routePath = path.slice(projectPrefix.length)

  for (const [legacyPath, currentPath] of Object.entries(legacyRedirects)) {
    if (!routePath.startsWith(legacyPath)) {
      continue
    }
    // Only match a full segment, to leave e.g. `${legacyPath}-report` alone.
    const nextChar = routePath[legacyPath.length]
    if (nextChar === undefined || nextChar === '/') {
      const suffix = fullPath.slice(projectPrefix.length + legacyPath.length)
      const target = `${projectPrefix}${currentPath === '/' ? '' : currentPath}${suffix}`
      // A root target may collapse to nothing (or a bare query/hash) — re-anchor it.
      return target === '' || target.startsWith('?') || target.startsWith('#') ? `/${target}` : target
    }
  }
  return null
}
